import { Service } from '@/services/api';
import {
  Document,
  DocumentType,
  DocumentDomain,
  DocumentTag,
  UploadDocumentRequest,
  DocumentWithOtherData,
  UpdateDocumentRequest,
} from '@/types/v2/Document';

export interface GetDocumentsRequest {
  documentDomainId?: number;
  documentTypeId?: number;
  accountId?: number;
}

export interface ExtractionStatus {
  hasValidatedExtraction: boolean;
  policyId?: number;
}

class DocumentService extends Service {
  async getDocuments(request: GetDocumentsRequest): Promise<Document[]> {
    return await this.get<Document[]>('/v2/documents', { ...request }).then(this.handleResponse);
  }

  async getClientVisibleDocumentCountByAccount(accountId: number, isParent: boolean = false): Promise<number> {
    return await this.get<number>(`/v2/accounts/${accountId}/documents/client-visible-count`, { isParent }).then(this.handleResponse);
  }

  async getDocumentFileById(documentId: string): Promise<string> {
    return await this.get(`/v2/document-files/${documentId}`, undefined, {
      responseType: 'blob',
    }).then(this.handleFile);
  }

  async getDocumentsByAccount(accountId: number): Promise<Document[]> {
    return await this.get<Document[]>(`/v2/accounts/${accountId}/documents`).then(this.handleResponse);
  }

  async getDocumentsWithOtherDataByAccount(accountId: number): Promise<DocumentWithOtherData[]> {
    return await this.get<DocumentWithOtherData[]>(`/v2/accounts/${accountId}/documents-with-other-data`).then(
      this.handleResponse,
    );
  }

  async getDocumentsByPolicy(accountId: number, policyId: number | string): Promise<Document[]> {
    return await this.get<Document[]>(`/v2/accounts/${accountId}/policies/${policyId}/documents`).then(
      this.handleResponse,
    );
  }

  async getDocumentsUnderParentAccount(parentAccountId: number): Promise<Document[]> {
    return await this.get<Document[]>(`/v2/parent-accounts/${parentAccountId}/documents`).then(this.handleResponse);
  }

  async getDocumentsWithOtherDataUnderParentAccount(parentAccountId: number): Promise<DocumentWithOtherData[]> {
    return await this.get<DocumentWithOtherData[]>(
      `/v2/parent-accounts/${parentAccountId}/documents-with-other-data`,
    ).then(this.handleResponse);
  }

  async togglePinned(documentId: string) {
    return await this.patch(`/v2/documents/${documentId}/toggle-pinned`, documentId).then(this.handleResponse);
  }

  async deleteDocumentByIdAndAccount(accountId: number, documentId: string): Promise<void> {
    await this.delete(`/v2/accounts/${accountId}/document-files/${documentId}`).then(this.handleResponse);
  }
  
  async deleteDocumentById(documentId: string): Promise<void> {
    await this.delete(`/v2/document-files/${documentId}`).then(this.handleResponse);
  }

  async getExtractionStatus(accountId: number | null, documentId: string): Promise<ExtractionStatus> {
    const url = accountId
      ? `/v2/accounts/${accountId}/document-files/${documentId}/extraction-status`
      : `/v2/document-files/${documentId}/extraction-status`;
    return await this.get<ExtractionStatus>(url).then(this.handleResponse);
  }

  async updateDocument(documentId: string, request: UpdateDocumentRequest): Promise<void> {
    await this.patch(`/v2/documents/${documentId}`, request).then(this.handleResponse);
  }

  /**
   * Upload a document for a policy or account.
   */
  async uploadDocumentForPolicyOrAccount(request: UploadDocumentRequest): Promise<void> {
    const formData = new FormData();
    formData.append('file', request.document);
    formData.append(
      'request',
      JSON.stringify({
        domain: request.domain,
        type: request.type,
        displayName: request.displayName,
        fileName: request.fileName,
        policyId: request.policyId,
        analyticsTypeId: request.analyticsTypeId,
        tags: request.tags,
      }),
    );

    await this.post(`/v2/accounts/${request.accountId}/document-files`, formData).then(this.handleResponse);
  }

  /**
   * Upload a document. The policyId and the accountId are optional.
   * This is used for uploading documents that are not associated with a policy or account.
   * Such as analytics documents.
   */
  async uploadDocument(request: UploadDocumentRequest): Promise<void> {
    const formData = new FormData();
    formData.append('file', request.document);
    formData.append(
      'request',
      JSON.stringify({
        domain: request.domain,
        type: request.type,
        displayName: request.displayName,
        fileName: request.fileName,
        accountId: request.accountId,
        policyId: request.policyId,
        analyticsTypeId: request.analyticsTypeId,
        tags: request.tags,
      }),
    );

    await this.post('/v2/documents', formData).then(this.handleResponse);
  }

  async getDocumentTypes(): Promise<DocumentType[]> {
    return await this.get<DocumentType[]>('/v2/document-types').then(this.handleResponse);
  }

  async getDocumentDomains(): Promise<DocumentDomain[]> {
    return await this.get<DocumentDomain[]>('/v2/document-domains').then(this.handleResponse);
  }

  async getDocumentTags(): Promise<DocumentTag[]> {
    return await this.get<DocumentTag[]>('/v2/documents/tags').then(this.handleResponse);
  }

  async publishDocument(documentId: string): Promise<void> {
    await this.patch<void>(`/v2/documents/${documentId}`, { published: true }).then(this.handleResponse);
  }

  async unPublishDocument(documentId: string): Promise<void> {
    await this.patch<void>(`/v2/documents/${documentId}`, { published: false }).then(this.handleResponse);
  }

  async startAIExtraction(
    accountId: number,
    documentIds: string[],
  ): Promise<{ enqueuedCount: number; failedDocumentIds: string[] }> {
    return await this.post<{ enqueuedCount: number; failedDocumentIds: string[] }>(
      `/v2/accounts/${accountId}/documents/ai-extraction`,
      { documentIds },
    ).then(this.handleResponse);
  }

  async downloadDocumentFile(accountId: number, documentId: string, convertToPdf: boolean = false): Promise<string> {
    return await this.get(`/v2/accounts/${accountId}/document-files/${documentId}`, { convertToPdf }, {
      responseType: 'blob',
    }).then(this.handleFile);
  }

  async downloadPolicyTemplate(): Promise<string> {
    return await this.get(`/v2/documents/policy-template`, undefined, {
      responseType: 'blob',
    }).then(this.handleFile);
  }
}

export const documentService = new DocumentService();
